import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import type { ConversationDebugDTO } from "@shaarp/common";
import { DebugLoading } from "./debug-tab-parts";
import { formatBytesCompact, formatDate, fullNumber } from "./format";

type ToolsTabProps = {
  debug: ConversationDebugDTO | undefined;
  isLoading: boolean;
};

/**
 * What the conversation's tools remember — the store that stops a follow-up from
 * paying for a search twice, and the first place to look when it did anyway.
 *
 * Inputs only, never what they returned. `scrapeWebsite` keeps the extracted
 * text of every page it read, so listing the values here would put third-party
 * page content on screen and, worse, in the report this panel offers to copy
 * into an agent. The API sends metadata for the same reason; the values are
 * readable with `pnpm --filter @shaarp/api check:tool-memory <conversationId>`
 * by whoever already has database access.
 */
export function ToolsTab({ debug, isLoading }: ToolsTabProps) {
  const { t } = useTranslation();

  if (isLoading) return <DebugLoading />;
  if (!debug) return null;

  if (debug.toolMemory.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("chat.debug.tools.none")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {debug.toolMemory.map((memory) => (
        <div className="space-y-2 rounded-md border p-3" key={memory.toolName}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm">{memory.toolName}</span>
            <Badge variant="secondary">
              {formatBytesCompact(memory.sizeBytes)}
            </Badge>
            <span className="ml-auto font-mono text-muted-foreground text-xs">
              {formatDate(memory.updatedAt)}
            </span>
          </div>

          {memory.entries.length > 0 ? (
            <div className="space-y-1">
              <p className="text-muted-foreground text-xs">
                {t("chat.debug.tools.entries", {
                  listed: fullNumber(memory.entries.length),
                })}
                {memory.entriesOmitted > 0
                  ? ` · ${t("chat.debug.tools.entriesOmitted", {
                      count: fullNumber(memory.entriesOmitted),
                    })}`
                  : ""}
              </p>
              <ul className="space-y-0.5">
                {memory.entries.map((entry) => (
                  <li
                    className="truncate font-mono text-xs"
                    key={entry}
                    title={entry}
                  >
                    {entry}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-muted-foreground text-xs">
              {t("chat.debug.tools.notKeyed")}{" "}
              <span className="font-mono">{memory.fields.join(", ")}</span>
            </p>
          )}
        </div>
      ))}

      <p className="text-muted-foreground text-xs">
        {t("chat.debug.tools.footer")}
      </p>
    </div>
  );
}
